/**
 * TemplateCover — renderiza la portada visual de una plantilla.
 *
 * Recibe un `TemplateCover` (configuración) + size + showText y arma el visual
 * usando uno de 8 layouts. Cada layout es una composición CSS distinta entre
 * imagen, color, tipografía y posición.
 *
 * Modos:
 *   - "thumb": tamaño card de galería (label corto)
 *   - "preview": tamaño modal preview (label + tagline)
 *   - "page": tamaño de página A4 dentro del editor (texto completo)
 */
import { type CSSProperties } from "react";
import type { PlanTemplate, TemplateCover as TemplateCoverData } from "../../types/templates";

type Size = "thumb" | "preview" | "page";

interface TemplateCoverProps {
  template: PlanTemplate;
  size?: Size;
  className?: string;
}

function fontStack(name: string): string {
  if (/playfair/i.test(name)) return "'Playfair Display', Georgia, serif";
  if (/bricolage/i.test(name)) return "'Bricolage Grotesque', system-ui, sans-serif";
  if (/montserrat/i.test(name)) return "'Montserrat', system-ui, sans-serif";
  if (/oswald/i.test(name)) return "'Oswald', 'Bebas Neue', system-ui, sans-serif";
  if (/jetbrains/i.test(name)) return "'JetBrains Mono', ui-monospace, monospace";
  return "'Inter Tight', 'Inter', system-ui, sans-serif";
}

export function TemplateCover({ template, size = "thumb", className }: TemplateCoverProps) {
  const cv = template.cover;
  // Si no hay coverVisual definido (templates sin migrar), fallback simple.
  if (!cv) {
    return <FallbackCover template={template} className={className} />;
  }
  const display = fontStack(template.typography.display);
  const body = fontStack(template.typography.body);
  const ctx: LayoutCtx = { tpl: template, cv, size, display, body };

  switch (cv.layout) {
    case "hero-bottom":     return <HeroBottomLayout ctx={ctx} className={className} />;
    case "hero-top":        return <HeroTopLayout ctx={ctx} className={className} />;
    case "split-vertical":  return <SplitVerticalLayout ctx={ctx} className={className} />;
    case "split-diagonal":  return <SplitDiagonalLayout ctx={ctx} className={className} />;
    case "frame-border":    return <FrameBorderLayout ctx={ctx} className={className} />;
    case "magazine":        return <MagazineLayout ctx={ctx} className={className} />;
    case "minimal-icon":    return <MinimalIconLayout ctx={ctx} className={className} />;
    case "polaroid":        return <PolaroidLayout ctx={ctx} className={className} />;
    default:                return <HeroBottomLayout ctx={ctx} className={className} />;
  }
}

interface LayoutCtx {
  tpl: PlanTemplate;
  cv: TemplateCoverData;
  size: Size;
  display: string;
  body: string;
}

interface LayoutProps {
  ctx: LayoutCtx;
  className?: string;
}

// ----------------------------------------------------------------------
// Fallback: cuando un template no tiene coverVisual aún
// ----------------------------------------------------------------------

function FallbackCover({ template, className }: { template: PlanTemplate; className?: string }) {
  const display = fontStack(template.typography.display);
  return (
    <div className={"al-tcover " + (className || "")} style={{ background: template.palette.bg }}>
      <div className="al-tcover-inner" style={{
        height: "100%", padding: 16, display: "flex", flexDirection: "column",
        justifyContent: "space-between", color: template.palette.ink,
      }}>
        <div className="tiny" style={{ color: template.palette.primary, fontWeight: 700, letterSpacing: ".1em" }}>
          {template.category.toUpperCase()}
        </div>
        <div style={{ fontFamily: display, fontSize: "1.4em", fontWeight: 700, lineHeight: 1.1 }}>
          {template.label}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <span style={{ width: 16, height: 16, borderRadius: 4, background: template.palette.primary }} />
          <span style={{ width: 16, height: 16, borderRadius: 4, background: template.palette.accent }} />
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Helpers comunes a todos los layouts
// ----------------------------------------------------------------------

function imageBg(cv: TemplateCoverData): CSSProperties {
  return {
    backgroundImage: `url(${cv.imageUrl})`,
    backgroundSize: "cover",
    backgroundPosition: cv.imagePosition || "center center",
    backgroundColor: "#1a1a1a",
  };
}

function overlayLayer(cv: TemplateCoverData, primary: string, accent: string): CSSProperties {
  switch (cv.overlay) {
    case "dark-gradient":
      return {
        background: "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.85) 100%)",
      };
    case "color-tint":
      return {
        background: `linear-gradient(135deg, ${hexA(primary, 0.55)} 0%, ${hexA(accent, 0.35)} 100%)`,
        mixBlendMode: "multiply",
      } as CSSProperties;
    case "duotone":
      return {
        background: `linear-gradient(135deg, ${primary} 0%, ${accent} 100%)`,
        mixBlendMode: "color",
        opacity: 0.65,
      } as CSSProperties;
    case "split":
      return {
        background: `linear-gradient(90deg, transparent 50%, ${hexA(primary, 0.85)} 50%)`,
      };
    case "none":
    default:
      return {};
  }
}

/** Convierte hex (#rrggbb) a rgba con alpha. */
function hexA(hex: string, a: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!m) return `rgba(0,0,0,${a})`;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  return `rgba(${r},${g},${b},${a})`;
}

function titleSize(size: Size): string {
  return size === "thumb" ? "1.25em" : size === "preview" ? "2em" : "2.6em";
}

function eyebrowSize(size: Size): string {
  return size === "thumb" ? "0.62em" : size === "preview" ? "0.78em" : "0.9em";
}

function paddingFor(size: Size): string {
  return size === "thumb" ? "12px" : size === "preview" ? "20px" : "32px";
}

// ----------------------------------------------------------------------
// LAYOUT 1: Hero Bottom — imagen full + banda con título abajo
// ----------------------------------------------------------------------

function HeroBottomLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  return (
    <div className={"al-tcover hero-bottom " + (className || "")} style={{ position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, ...imageBg(cv) }} />
      <div style={{ position: "absolute", inset: 0, ...overlayLayer(cv, tpl.palette.primary, tpl.palette.accent) }} />
      <div
        style={{
          position: "absolute",
          left: 0, right: 0, bottom: 0,
          padding: paddingFor(size),
          background: `linear-gradient(180deg, transparent, ${tpl.palette.primary}f0)`,
          color: "#fff",
        }}
      >
        <div style={{ fontSize: eyebrowSize(size), letterSpacing: ".15em", opacity: 0.92, fontWeight: 600 }}>
          {tpl.category.toUpperCase()}
        </div>
        <div style={{ fontFamily: display, fontSize: titleSize(size), fontWeight: 800, lineHeight: 1.05, marginTop: 4 }}>
          {tpl.label}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// LAYOUT 2: Hero Top — banda con título arriba + imagen abajo
// ----------------------------------------------------------------------

function HeroTopLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  return (
    <div className={"al-tcover hero-top " + (className || "")} style={{ position: "relative", overflow: "hidden", background: tpl.palette.primary }}>
      <div
        style={{
          padding: paddingFor(size),
          color: "#fff",
          height: "40%",
          display: "flex", flexDirection: "column", justifyContent: "flex-end",
        }}
      >
        <div style={{ fontSize: eyebrowSize(size), letterSpacing: ".15em", opacity: 0.85, fontWeight: 600 }}>
          {tpl.category.toUpperCase()}
        </div>
        <div style={{ fontFamily: display, fontSize: titleSize(size), fontWeight: 800, lineHeight: 1.05, marginTop: 4 }}>
          {tpl.label}
        </div>
      </div>
      <div style={{ position: "relative", height: "60%", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, ...imageBg(cv) }} />
        <div style={{ position: "absolute", inset: 0, ...overlayLayer(cv, tpl.palette.primary, tpl.palette.accent) }} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// LAYOUT 3: Split Vertical — 50% imagen + 50% color sólido
// ----------------------------------------------------------------------

function SplitVerticalLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  return (
    <div className={"al-tcover split-vertical " + (className || "")} style={{ position: "relative", overflow: "hidden", display: "flex" }}>
      <div style={{ flex: 1, position: "relative" }}>
        <div style={{ position: "absolute", inset: 0, ...imageBg(cv) }} />
        <div style={{ position: "absolute", inset: 0, ...overlayLayer(cv, tpl.palette.primary, tpl.palette.accent) }} />
      </div>
      <div
        style={{
          flex: 1,
          background: tpl.palette.primary,
          color: "#fff",
          padding: paddingFor(size),
          display: "flex", flexDirection: "column", justifyContent: "center",
        }}
      >
        <div style={{ fontSize: eyebrowSize(size), letterSpacing: ".15em", opacity: 0.85, fontWeight: 600 }}>
          {tpl.category.toUpperCase()}
        </div>
        <div style={{ fontFamily: display, fontSize: titleSize(size), fontWeight: 800, lineHeight: 1.05, marginTop: 6 }}>
          {tpl.label}
        </div>
        <div style={{
          width: 40, height: 3, background: tpl.palette.accent, marginTop: 12,
        }} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// LAYOUT 4: Split Diagonal — diagonal partida entre imagen y color
// ----------------------------------------------------------------------

function SplitDiagonalLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  return (
    <div className={"al-tcover split-diagonal " + (className || "")} style={{ position: "relative", overflow: "hidden", background: tpl.palette.primary }}>
      <div style={{ position: "absolute", inset: 0, ...imageBg(cv), clipPath: "polygon(0 0, 65% 0, 35% 100%, 0 100%)" }} />
      <div style={{ position: "absolute", inset: 0, ...overlayLayer(cv, tpl.palette.primary, tpl.palette.accent), clipPath: "polygon(0 0, 65% 0, 35% 100%, 0 100%)" }} />
      <div
        style={{
          position: "absolute", right: 0, top: 0, bottom: 0,
          width: "55%",
          padding: paddingFor(size),
          color: "#fff",
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-end",
          textAlign: "right",
        }}
      >
        <div style={{ fontSize: eyebrowSize(size), letterSpacing: ".15em", opacity: 0.92, fontWeight: 600 }}>
          {tpl.category.toUpperCase()}
        </div>
        <div style={{ fontFamily: display, fontSize: titleSize(size), fontWeight: 800, lineHeight: 1.05, marginTop: 6 }}>
          {tpl.label}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// LAYOUT 5: Frame Border — imagen con marco grueso de color
// ----------------------------------------------------------------------

function FrameBorderLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  const border = size === "thumb" ? 8 : size === "preview" ? 14 : 22;
  return (
    <div
      className={"al-tcover frame-border " + (className || "")}
      style={{
        position: "relative", overflow: "hidden",
        background: tpl.palette.primary,
        padding: border,
      }}
    >
      <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, ...imageBg(cv) }} />
        <div style={{ position: "absolute", inset: 0, ...overlayLayer(cv, tpl.palette.primary, tpl.palette.accent) }} />
        <div
          style={{
            position: "absolute", left: 0, right: 0, bottom: 0,
            padding: paddingFor(size),
            background: "linear-gradient(180deg, transparent, rgba(0,0,0,0.7))",
            color: "#fff",
          }}
        >
          <div style={{ fontSize: eyebrowSize(size), letterSpacing: ".15em", opacity: 0.9, fontWeight: 600 }}>
            {tpl.category.toUpperCase()}
          </div>
          <div style={{ fontFamily: display, fontSize: titleSize(size), fontWeight: 800, lineHeight: 1.05, marginTop: 4 }}>
            {tpl.label}
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// LAYOUT 6: Magazine — imagen full blur + título overlay tipo revista
// ----------------------------------------------------------------------

function MagazineLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  return (
    <div className={"al-tcover magazine " + (className || "")} style={{ position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, ...imageBg(cv), filter: "brightness(0.55)" }} />
      <div style={{ position: "absolute", inset: 0, ...overlayLayer(cv, tpl.palette.primary, tpl.palette.accent) }} />
      <div
        style={{
          position: "absolute", inset: 0,
          padding: paddingFor(size),
          color: "#fff",
          display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: eyebrowSize(size), letterSpacing: ".25em", fontWeight: 700,
            color: tpl.palette.accent, marginBottom: 8,
          }}
        >
          {tpl.category.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: display,
            fontSize: titleSize(size), fontWeight: 900,
            lineHeight: 1, textTransform: "uppercase", letterSpacing: "-0.03em",
            maxWidth: "90%",
          }}
        >
          {tpl.label}
        </div>
        <div style={{
          width: size === "thumb" ? 24 : 48, height: 2,
          background: tpl.palette.accent, marginTop: 10,
        }} />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// LAYOUT 7: Minimal Icon — fondo color + icono SVG grande centrado
// ----------------------------------------------------------------------

function MinimalIconLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  const iconSize = size === "thumb" ? 56 : size === "preview" ? 96 : 140;
  // Default icon path si no viene en cv: una "estrella" abstracta
  const iconPath = cv.iconSvg || "M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 16.8 5.8 21.3l2.4-7.4L2 9.4h7.6z";
  return (
    <div
      className={"al-tcover minimal-icon " + (className || "")}
      style={{
        position: "relative", overflow: "hidden",
        background: `linear-gradient(135deg, ${tpl.palette.primary} 0%, ${tpl.palette.accent} 100%)`,
        color: "#fff",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: paddingFor(size),
        textAlign: "center",
      }}
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
        <path d={iconPath} />
      </svg>
      <div style={{ fontSize: eyebrowSize(size), letterSpacing: ".2em", opacity: 0.85, marginTop: 14, fontWeight: 600 }}>
        {tpl.category.toUpperCase()}
      </div>
      <div style={{ fontFamily: display, fontSize: titleSize(size), fontWeight: 800, lineHeight: 1.05, marginTop: 4 }}>
        {tpl.label}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// LAYOUT 8: Polaroid — fondo color + foto pequeña como instantánea rotada
// ----------------------------------------------------------------------

function PolaroidLayout({ ctx, className }: LayoutProps) {
  const { tpl, cv, size, display } = ctx;
  const polaroidSize = size === "thumb" ? 90 : size === "preview" ? 160 : 220;
  return (
    <div
      className={"al-tcover polaroid " + (className || "")}
      style={{
        position: "relative", overflow: "hidden",
        background: `linear-gradient(160deg, ${tpl.palette.primary} 0%, ${tpl.palette.accent} 110%)`,
        padding: paddingFor(size),
        color: "#fff",
        display: "flex", flexDirection: "column", justifyContent: "space-between",
      }}
    >
      <div>
        <div style={{ fontSize: eyebrowSize(size), letterSpacing: ".2em", opacity: 0.92, fontWeight: 600 }}>
          {tpl.category.toUpperCase()}
        </div>
        <div style={{ fontFamily: display, fontSize: titleSize(size), fontWeight: 800, lineHeight: 1.05, marginTop: 6 }}>
          {tpl.label}
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
        <div
          style={{
            width: polaroidSize, height: polaroidSize,
            background: "#fff",
            padding: 6,
            paddingBottom: size === "thumb" ? 14 : 22,
            transform: "rotate(3deg)",
            boxShadow: "0 8px 24px -4px rgba(0,0,0,0.32)",
          }}
        >
          <div
            style={{ width: "100%", height: "100%", ...imageBg(cv) }}
          />
        </div>
      </div>
    </div>
  );
}
